'use client';

import { createContext, useContext, useEffect, useLayoutEffect, useRef, useSyncExternalStore, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { getConsentSnapshot, getServerConsentSnapshot, subscribeConsent } from '@/contexts/consent/store';
import { cgpClient } from '@/contexts/cgp/client';

type CgpContextValue = Pick<typeof cgpClient, 'track' | 'page' | 'identify' | 'resetIdentity' | 'confirmAnonymous'>;
const CgpContext = createContext<CgpContextValue>({
    track: () => false,
    page: () => false,
    identify: () => {},
    resetIdentity: () => {},
    confirmAnonymous: () => {},
});

interface CgpProviderProps {
    /** 应用子树，不等待 CGP SDK 或数据面连接。 */
    children: ReactNode;
}

/** 缺省按当前 pathname 计页；App 可传入归一后的计页路径（如路由模板）。 */
export function CgpPageTracker({ path: pagePath }: { /** 覆盖计页路径。 */ path?: string } = {}) {
    const pathname = usePathname();
    const path = pagePath ?? pathname;
    const { page } = useCgp();
    const { analyticsAllowed } = useSyncExternalStore(subscribeConsent, getConsentSnapshot, getServerConsentSnapshot);
    const lastPath = useRef<string | null>(null);
    useEffect(() => {
        if (!analyticsAllowed) { lastPath.current = null; return; }
        if (path && lastPath.current !== path && page(path)) lastPath.current = path;
    }, [analyticsAllowed, page, path]);
    return null;
}

export function CgpProvider({ children }: CgpProviderProps) {
    useLayoutEffect(() => {
        const unsubscribe = subscribeConsent(cgpClient.syncConsent);
        void cgpClient.start();
        return () => { unsubscribe(); cgpClient.dispose(); };
    }, []);
    return <CgpContext.Provider value={cgpClient}>{children}</CgpContext.Provider>;
}

export function useCgp(): CgpContextValue {
    return useContext(CgpContext);
}
